import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Crop, Image as ImageIcon, X, Wand2, Sparkles, ZoomIn } from "lucide-react";
import { processCover, coverAi, coverAiQuota } from "@/lib/afmApi";
import { useAuth } from "@/context/AuthContext";

const MODES = [
  { key: "crop", label: "Encuadre 1:1", icon: Crop, desc: "Recorta y encuadra la portada a cuadrado tú mismo." },
  { key: "blur", label: "Fondo desenfocado", icon: ImageIcon, desc: "Centra el arte y rellena el sobrante con un fondo desenfocado, sin deformar. Si ya es cuadrada, solo la amplía a 3000×3000." },
  { key: "ai_expand", label: "Completar con IA", icon: Sparkles, ai: "expand", desc: "La IA dibuja lo que falta a los lados para volverla cuadrada sin recortar tu arte. Verás el resultado antes de usarlo." },
  { key: "ai_upscale", label: "Mejorar con IA", icon: ZoomIn, ai: "upscale", desc: "Para imágenes pequeñas: la IA la recrea con más detalle en 3000×3000. Verás el resultado antes de usarlo." },
];

// Opens an in-app editor (Carátulas tech) to fix a non-compliant cover and process it to 3000x3000.
// onApply(result) receives { image: dataURL, width, height, size_kb, ... }
export const CoverEditorModal = ({ file, onApply, onCancel }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [mode, setMode] = useState("crop");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const { user } = useAuth();
  const [quota, setQuota] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  useEffect(() => { if (user?.id) coverAiQuota(user.id).then(setQuota); }, [user?.id]);
  const current = MODES.find((m) => m.key === mode);
  const aiCostLabel = !quota ? "" : quota.remaining > 0 ? `Incluida (te quedan ${quota.remaining} este mes)`
    : quota.can_pay_with_gestiones ? `Usa ${quota.cost_gestiones} gestión${quota.cost_gestiones === 1 ? "" : "es"} (te quedan ${quota.gestiones_available})` : "Sin conversiones disponibles este mes";

  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    setImgUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const onCropComplete = useCallback((_a, areaPixels) => setCroppedPixels(areaPixels), []);

  const apply = async () => {
    if (current?.ai) {
      if (aiResult) { onApply(aiResult); return; }
      setProcessing(true);
      try {
        const r = await coverAi(user.id, file, current.ai);
        setAiResult(r); if (r.quota) setQuota(r.quota);
      } catch (e) { toast.error(e?.response?.data?.detail || "La IA no pudo procesar la portada"); }
      finally { setProcessing(false); }
      return;
    }
    setProcessing(true);
    try {
      const crp = mode === "crop" ? croppedPixels : null;
      const result = await processCover(file, mode, crp);
      onApply(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo procesar la imagen");
    } finally { setProcessing(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="cover-editor-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="font-serif text-lg font-bold text-black">Ajuste rápido de portada</h3>
            <p className="text-xs text-slate-500">Tu imagen no cumple 1:1 o 3000×3000. Elige cómo arreglarla; si ninguna te gusta, sube otra o pide una portada en Carátulas.</p>
          </div>
          <button data-testid="cover-editor-close" onClick={onCancel} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-black"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6">
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {MODES.map((m) => (
              <button
                key={m.key}
                data-testid={`cover-editor-mode-${m.key}`}
                onClick={() => { setMode(m.key); setAiResult(null); }}
                className={`flex flex-1 items-center gap-2 rounded-xl border p-3 text-left text-sm transition-all ${mode === m.key ? "border-black bg-black text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
              >
                <m.icon className="h-4 w-4 shrink-0" />
                <span className="font-semibold">{m.label}</span>
              </button>
            ))}
          </div>
          <p className="mb-3 text-xs text-slate-500">{MODES.find((m) => m.key === mode)?.desc}</p>

          {current?.ai && aiCostLabel && <p className="mb-3 text-xs font-semibold text-[#AC3333]">{aiCostLabel}</p>}
          {current?.ai && aiResult ? (
            <div className="grid h-[320px] grid-cols-2 gap-3">
              <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-2"><span className="mb-1 text-[11px] text-slate-500">Original</span>{imgUrl && <img src={imgUrl} alt="original" className="max-h-[270px] max-w-full object-contain" />}</div>
              <div className="flex flex-col items-center justify-center rounded-xl border-2 border-emerald-400 bg-slate-50 p-2"><span className="mb-1 text-[11px] font-semibold text-emerald-700">Con IA · 3000×3000</span><img src={aiResult.image} alt="resultado" className="max-h-[270px] max-w-full object-contain" /></div>
            </div>
          ) : mode === "crop" ? (
            <div className="relative h-[320px] w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-900">
              {imgUrl && (
                <Cropper image={imgUrl} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} showGrid />
              )}
            </div>
          ) : (
            <div className="flex h-[320px] w-full items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {imgUrl && <img src={imgUrl} alt="preview" className="max-h-full max-w-full object-contain" />}
            </div>
          )}

          {mode === "crop" && (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Zoom</span>
              <input data-testid="cover-editor-zoom" type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-black" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
          {current?.ai && aiResult && <button onClick={() => setAiResult(null)} className="mr-auto text-xs font-semibold text-slate-500 hover:text-black">Descartar resultado</button>}
          <button data-testid="cover-editor-cancel" onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium hover:border-black">Cancelar</button>
          <button data-testid="cover-editor-apply" onClick={apply} disabled={processing} className="flex items-center gap-2 rounded-lg bg-[#AC3333] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF0505] disabled:opacity-60">
            {processing ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : current?.ai ? <Sparkles className="h-4 w-4" /> : <Wand2 className="h-4 w-4" />}
            {current?.ai ? (aiResult ? "Usar esta portada" : processing ? "La IA está trabajando (hasta 1 min)…" : "Generar vista previa con IA") : "Ajustar, procesar a 3000×3000px y adjuntar"}
          </button>
        </div>
      </div>
    </div>
  );
};
